import {
  DEFAULT_PROJECT_CATEGORY_ID,
  PROJECT_CATEGORIES,
} from "../constants/projectCategories";
import { stripHtml } from "../services/wpPagesApi";
import { getProjectAcf, getProjectGalleryItems } from "./projectMedia";

function normalizeToken(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function matchCategoryId(raw) {
  const token = normalizeToken(raw);
  if (!token) return null;

  for (const category of PROJECT_CATEGORIES) {
    if (category.id === token.replace(/\s+/g, "")) {
      return category.id;
    }

    if (normalizeToken(category.labelKey) === token) {
      return category.id;
    }

    if (category.matchers.some((matcher) => normalizeToken(matcher) === token)) {
      return category.id;
    }

    if (
      category.matchers.some(
        (matcher) =>
          token.includes(normalizeToken(matcher)) ||
          normalizeToken(matcher).includes(token),
      )
    ) {
      return category.id;
    }
  }

  return null;
}

function readCategoryCandidate(value) {
  if (typeof value === "string" || typeof value === "number") {
    return matchCategoryId(value);
  }

  if (value && typeof value === "object") {
    return matchCategoryId(value.slug || value.name || value.label || value.value);
  }

  return null;
}

function readEmbeddedTermCategory(project, termIds) {
  const embeddedTerms = project?._embedded?.["wp:term"]?.flat() || [];
  const term = embeddedTerms.find((entry) => termIds.includes(entry.id));

  if (!term) return null;
  return matchCategoryId(term.slug || term.name);
}

export function extractProjectCategoryId(project) {
  const acf = getProjectAcf(project);
  const acfCandidates = [
    acf.category,
    acf.project_category,
    acf.gallery_category,
    acf.flooring_type,
    acf.type,
  ];

  for (const candidate of acfCandidates) {
    const categoryId = readCategoryCandidate(candidate);
    if (categoryId) return categoryId;
  }

  for (const [key, value] of Object.entries(project)) {
    if (!Array.isArray(value) || value.length === 0) continue;
    if (!key.includes("categor") && !key.includes("type")) continue;
    if (typeof value[0] !== "number") continue;

    const categoryId = readEmbeddedTermCategory(project, value);
    if (categoryId) return categoryId;
  }

  const titleCategoryId = matchCategoryId(stripHtml(project?.title?.rendered || ""));
  if (titleCategoryId) return titleCategoryId;

  return null;
}

export function buildProjectGalleries(projects = [], mediaMap = {}, { lang = "ar" } = {}) {
  const buckets = Object.fromEntries(
    PROJECT_CATEGORIES.map((category) => [
      category.id,
      {
        id: category.id,
        labelKey: category.labelKey,
        images: [],
      },
    ]),
  );
  const seenUrls = Object.fromEntries(
    PROJECT_CATEGORIES.map((category) => [category.id, new Set()]),
  );

  projects.forEach((project) => {
    const categoryId = extractProjectCategoryId(project) || DEFAULT_PROJECT_CATEGORY_ID;
    if (!buckets[categoryId]) return;

    getProjectGalleryItems(project, mediaMap, { lang }).forEach((item) => {
      if (!item.url || seenUrls[categoryId].has(item.url)) return;

      seenUrls[categoryId].add(item.url);
      buckets[categoryId].images.push({
        id: item.id,
        url: item.url,
        alt: item.alt || item.title || "",
        title: item.title || "",
        width: item.width || null,
        height: item.height || null,
      });
    });
  });

  return PROJECT_CATEGORIES.map((category) => buckets[category.id]);
}
